/**
 * @file Manual Match Reducer.
 */
import uniqBy from 'lodash.uniqby';

import {
  MANUAL_MATCH_FETCH_SELECTED_IDENTITY_REQUEST,
  MANUAL_MATCH_FETCH_SELECTED_IDENTITY_SUCCESS,
  MANUAL_MATCH_VOTES_CAST_REQUEST,
  MANUAL_MATCH_VOTES_CAST_SUCCESS,
  MANUAL_MATCH_VOTES_CAST_FAILURE,
  MANUAL_MATCH_SELECT_PHOTOS,
  MANUAL_MATCH_DESELECT_PHOTOS,
  MANUAL_MATCH_NEXT_VOTE,
} from '../actions/manual-match-actions';
import {
  QUEUE_EXTEND_LEASES,
  QUEUE_DROP_LEASES,
} from '../actions/queue-actions';
import { extendLeaseBatch } from '../../utilities/leasing';

const initialState = {
  queue: [],
  loading: false,
  error: null,
  castingVotes: false,
};

/**
 * Helper factory for creating vote batches for a manual matching queue. This is to
 * ensure consistency and provide data schema clarity.
 *
 * @param {Object} batch - The batch.
 * @returns {{leaseId: string, expiresAt: number, selectedIdentity: Object, selectedPhotos: Array<Object>, faceMatchVotes: Array<Object>, activeFaceMatchVoteIndex: Number, castedVotes: Array<Object>}}
 */
const createVoteBatch = batch => {
  const { selectedIdentity, activeFaceMatchVoteIndex = 0 } = batch;

  return {
    // All batches have this info.
    leaseId: batch.leaseId,
    expiresAt: batch.expiresAt,
    // No harm in having the selected Identity around.
    selectedIdentity,
    selectedPhotos: [],
    faceMatchVotes: selectedIdentity?.face_match_votes?.records ?? [],
    activeFaceMatchVoteIndex,
    castedVotes: [],
    lastVoteIndex:
      activeFaceMatchVoteIndex - 1 < 0
        ? (selectedIdentity?.face_match_votes?.records ?? []).length - 1
        : activeFaceMatchVoteIndex - 1,
  };
};

const manualMatchReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case MANUAL_MATCH_FETCH_SELECTED_IDENTITY_REQUEST:
      return {
        ...state,
        loading: true,
      };

    case MANUAL_MATCH_FETCH_SELECTED_IDENTITY_SUCCESS: {
      const lease = createVoteBatch({
        leaseId: action.lease.lease_id,
        expiresAt: action.lease.expiresAt,
        selectedIdentity: action.lease.selectedIdentity,
        activeFaceMatchVoteIndex: action.lease.activeFaceMatchVoteIndex,
      });

      return {
        ...state,
        loading: false,
        queue: [lease, ...state.queue],
      };
    }

    case MANUAL_MATCH_SELECT_PHOTOS: {
      const { photos } = action;
      const [batch, ...rest] = state.queue;

      const { faceMatchVotes = [] } = batch;
      const faceMatchVotesPhotoIds = faceMatchVotes.map(
        faceMatchVote => faceMatchVote?.compared_face?.photo?.id,
      );

      const updatedBatch = {
        ...batch,
        selectedPhotos: uniqBy(
          [...photos, ...batch.selectedPhotos].filter(
            photo => !!photo.id && !faceMatchVotesPhotoIds.includes(photo.id),
          ),
          photo => photo?.id,
        ),
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case MANUAL_MATCH_DESELECT_PHOTOS: {
      const { photos } = action;
      const photoIds = photos
        .filter(photo => !!photo.id)
        .map(photo => photo.id);
      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        selectedPhotos: batch.selectedPhotos.filter(
          photo => !photoIds.includes(photo?.id),
        ),
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case MANUAL_MATCH_VOTES_CAST_REQUEST: {
      return {
        ...state,
        castingVotes: true,
      };
    }

    case MANUAL_MATCH_VOTES_CAST_SUCCESS: {
      const { photos, vote } = action;
      const castedVotesPhotosIds = photos.map(({ id }) => id);
      const castedVotes = photos
        .filter(photo => !!photo.id)
        .map(photo => ({
          photo,
          vote,
        }));

      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        selectedPhotos: batch.selectedPhotos.filter(
          photo => !castedVotesPhotosIds.includes(photo.id),
        ),
        castedVotes: uniqBy(
          [...castedVotes, ...batch.castedVotes],
          castedVoteItem => castedVoteItem.photo.id,
        ),
      };

      return {
        ...state,
        castingVotes: false,
        queue: [updatedBatch, ...rest],
      };
    }

    case MANUAL_MATCH_VOTES_CAST_FAILURE: {
      return {
        ...state,
        castingVotes: false,
        error: action.error,
      };
    }

    case MANUAL_MATCH_NEXT_VOTE: {
      const [batch, ...rest] = state.queue;

      const { faceMatchVotes = [], activeFaceMatchVoteIndex } = batch;

      const updatedBatch = {
        ...batch,
        selectedPhotos: [],
        activeFaceMatchVoteIndex:
          activeFaceMatchVoteIndex + 1 >= faceMatchVotes.length
            ? 0
            : activeFaceMatchVoteIndex + 1,
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case QUEUE_EXTEND_LEASES: {
      return {
        ...state,
        queue: state.queue.map(batch => extendLeaseBatch(batch, action)),
      };
    }

    case QUEUE_DROP_LEASES: {
      return {
        ...state,
        queue: state.queue.filter(
          batch => !action.leaseIds.includes(batch.leaseId),
        ),
      };
    }

    default:
      return state;
  }
};

export { initialState };
export default manualMatchReducer;
